import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx2DRuntime } from "../../vfx_2d_runtime.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { randomRange } from "../../../common/common_funcs.js";
import { radialOffset, randomPointInCircle, remap } from "../../../common/visual_fx_common_funcs.js";
import { GenericSpriteBatch } from "../../core/batching/generic_sprite_batch.js";
import { ColorMode } from "../../core/types/webgl_types.js";

interface ICoinParticle
{
  stable_id: number;
  from: Vector2;
  to: Vector2;
  size_max: number;
  start_time: number;
  duration: number;
}

export class VfxCoinCounterParticlesController implements IVfxTask
{
  private static readonly WAVES               : number = 2;
  private static readonly PARTICLES_PER_WAVE  : number = 10;
  private static readonly WAVE_DELAY_MS       : number = 100;
  private static readonly RADIUS_SCALE_FACTOR : number = 1.1;
  private static readonly MIN_SIZE            : number = 35;
  private static readonly MAX_SIZE            : number = 60;
  private static readonly ALPHA_BREAKPOINT    : number = 0.44;
  private static readonly SIZE_BREAKPOINT     : number = 0.38;

  private target: Vector2;
  private start_time: number;
  private duration: number;
  private particles: ICoinParticle[] = [];
  private render_on_back: boolean;
  private scale_in_duration: number;

  constructor(
    target: Vector2,
    target_size: number,
    life_span: number,
    start_time: number,
    render_on_back: boolean = false,
    scale_in_duration: number = 0
  )
  {
    this.target = target.clone();
    this.start_time = start_time;
    this.duration = life_span;
    this.render_on_back = render_on_back;
    this.scale_in_duration = scale_in_duration;

    const scale = LayoutScaler.getScale();
    const sprite_batch = this.spriteBatch();

    const spawn_radius = target_size * 0.5;
    const fly_rad = target_size * VfxCoinCounterParticlesController.RADIUS_SCALE_FACTOR;
    const fly_vec = new Vector2( fly_rad, fly_rad );
    const min_s = VfxCoinCounterParticlesController.MIN_SIZE * scale;
    const max_s = VfxCoinCounterParticlesController.MAX_SIZE * scale;

    for ( let w = 0; w < VfxCoinCounterParticlesController.WAVES; w++ )
    {
      const wave_start = this.start_time + w * VfxCoinCounterParticlesController.WAVE_DELAY_MS;

      for ( let i = 0; i < VfxCoinCounterParticlesController.PARTICLES_PER_WAVE; i++ )
      {
        const from = randomPointInCircle( this.target, spawn_radius );
        const offset = radialOffset( this.target, from, fly_vec );
        const to = from.add( offset );
        const particle_size = randomRange( min_s, max_s );

        // additive=0 (not 1): additive=1 makes the shader write alpha=0, which Safari's
        // canvas-to-page compositor renders as fully transparent on this alpha:true
        // WebGL canvas — invisible on Mac even though Chromium shows it fine.
        const stable_id = sprite_batch.spawnSprite(
          ImgID.CircleGlow3,
          from,
          new Vector2( particle_size, particle_size ),
          { x: 1.0, y: 0.8, z: 0.3 },
          0.0,
          0.0,
          0.0,
          wave_start,
          life_span,
          ColorMode.Mask,
        );

        if ( stable_id !== -1 )
        {
          this.particles.push( {
            stable_id,
            from,
            to,
            size_max: particle_size,
            start_time: wave_start,
            duration: life_span
          } );
        }
      }
    }
  }

  public onUpdate( now: number ): boolean
  {
    const sprite_batch = this.spriteBatch();

    let has_active_particles = false;
    for ( let i = 0; i < this.particles.length; i++ )
    {
      const p = this.particles[i];
      const elapsed = now - p.start_time;

      if ( elapsed >= 0 && elapsed < p.duration )
      {
        has_active_particles = true;
        const t = elapsed / p.duration;
        const reveal_progress = this.scale_in_duration > 0
          ? Math.min( 1, elapsed / this.scale_in_duration )
          : 1;
        const reveal_eased = 1 - ( 1 - reveal_progress ) * ( 1 - reveal_progress );
        const reveal_scale = 0.45 + 0.55 * reveal_eased;

        const unscaled_pos = Vector2.lerp( p.from, p.to, t );
        const current_pos = new Vector2(
          this.target.x + ( unscaled_pos.x - this.target.x ) * reveal_scale,
          this.target.y + ( unscaled_pos.y - this.target.y ) * reveal_scale
        );
        const current_size = p.size_max
          * reveal_scale
          * remap( t, VfxCoinCounterParticlesController.SIZE_BREAKPOINT, 1.0, 1.0, 0.0, true );
        const alpha = remap( t, VfxCoinCounterParticlesController.ALPHA_BREAKPOINT, 1.0, 1.0, 0.0, true );

        sprite_batch.updateSpriteData(
          p.stable_id,
          current_pos,
          new Vector2( current_size, current_size ),
          alpha,
          0.0
        );
      }
      else if ( elapsed >= p.duration )
      {
        sprite_batch.updateSpriteData( p.stable_id, p.to, new Vector2( 0, 0 ), 0.0, 0.0 );
        this.particles[i] = this.particles[this.particles.length - 1];
        this.particles.pop();
        i--;
      }
      else
      {
        has_active_particles = true;
      }
    }

    return !has_active_particles && ( now >= this.start_time + this.duration );
  }

  private spriteBatch(): GenericSpriteBatch
  {
    return this.render_on_back
      ? Vfx2DRuntime.onOverlayBack( GenericSpriteBatch )
      : Vfx2DRuntime.onOverlayFront( GenericSpriteBatch );
  }
}
